import instance from '../axios'

// Admin panel endpoints - server rejects callers whose UserTypeId isn't admin (2).
// Responses are camelCased by ASP.NET Core's default JSON policy.

export type AdminOverview = {
  totalUsers: number
  activeUsers: number
  deletedUsers: number
  newUsersThisMonth: number
  emailsSentLast7Days: number
  pendingEmailsBacklog: number
}

export type AdminUser = {
  id: number
  name: string
  username?: string | null
  emailAddress: string
  userTypeId: number
  isActive: boolean
  createdOnUtc: string
  lastLoginTimeUtc?: string | null
  deletedOnUtc?: string | null
  isSubscribedToEmailNotifications: boolean
  theme: string
  profileVisibility: string
  region: string
  defaultLandingPage: string
  spoilerControl: boolean
  showProgressIndicators: boolean
  hideTmdbReviews: boolean
  activityReminders: boolean
  recapAndMilestones: boolean
  emailNotifications: boolean
  newEpisodeAlerts: boolean
  newSeasonAlerts: boolean
  recommendations: boolean
  newReleases: boolean
  commentReplies: boolean
  notificationFrequency: string
  quietHoursEnabled: boolean
}

export type AdminEmailOptIns = {
  totalUsers: number
  subscribedToEmails: number
  emailNotifications: number
  newEpisodeAlerts: number
  newSeasonAlerts: number
  recommendations: number
  newReleases: number
  commentReplies: number
  activityReminders: number
  recapAndMilestones: number
  weeklyFrequency: number
  quietHoursEnabled: number
}

export type AdminMostTracked = {
  mediaType: string
  tmdbMediaId: number
  title: string
  userCount: number
}

export type AdminMostTrackedResponse = {
  movies: AdminMostTracked[]
  shows: AdminMostTracked[]
}

// Email types accepted by POST /admin/users/{id}/emails/{type}
export const ADMIN_EMAIL_TYPES = [
  { value: 'welcome', label: 'Welcome' },
  { value: 'monthly-recap', label: 'Monthly recap' },
  { value: 'year-in-review', label: 'Year in review' },
  { value: 'weekly-new-releases', label: 'Weekly new releases' },
  { value: 'trending', label: 'Trending now' },
  { value: 'top-rated', label: 'Top rated' },
] as const

export type AdminComment = {
  id: number
  userId: number
  authorName: string
  mediaType: string
  tmdbMediaId: number
  seasonNumber?: number | null
  episodeNumber?: number | null
  parentCommentId?: number | null
  content: string
  createdOnUtc: string
}

const adminApi = {
  getOverview: () => instance.get<AdminOverview>('/admin/overview'),
  getUsers: () => instance.get<AdminUser[]>('/admin/users'),
  getEmailOptIns: () => instance.get<AdminEmailOptIns>('/admin/email-optins'),
  getRecentComments: (take: number = 50) =>
    instance.get<AdminComment[]>(`/admin/comments?take=${take}`),
  deleteComment: (id: number) => instance.delete(`/admin/comments/${id}`),
  getMostTracked: (take: number = 10) =>
    instance.get<AdminMostTrackedResponse>(`/admin/most-tracked?take=${take}`),
  reopenUser: (id: number) => instance.post(`/admin/users/${id}/reopen`),
  // Sends through the real pipeline - respects the user's notification settings.
  sendUserEmail: (id: number, type: string) =>
    instance.post(`/admin/users/${id}/emails/${type}`),
  // Test push to the calling admin's own registered devices (pipeline check).
  sendTestPush: () => instance.post<{ message: string }>('/admin/test-push'),
}

export default adminApi
